import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Facebook, Youtube, Navigation, Menu, X, Info, MessageCircle } from "lucide-react";
import { BIZ, SERVICES, PRODUCTS, STEPS } from "./myrnaData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const TEAL = "#1E5B4F";
const TERRA = "#C77B3E";
const CREAM = "#FAF4E9";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function MyrnaSite() {
  const [open, setOpen] = useState(false);
  const links = [["Custom Pieces", "#custom"], ["Our Work", "#work"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#2A2318", fontFamily: "'Nunito', sans-serif" }} data-testid="myrna-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Nunito:wght@400;500;600;700&display=swap'); .myrna-serif{font-family:'Fraunces',serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: TERRA }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-[#FAF4E9]/95 backdrop-blur shadow-sm">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="myrna-logo" className="myrna-serif text-xl" style={{ color: TEAL }}>Myrna's Jewelry</a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-[#4A4030] hover:text-[#1E5B4F]">{l}</a>)}
          </div>
          <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="myrna-header-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ background: TERRA }}>Message Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="myrna-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-[#FAF4E9]">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — personalization-first, custom name jewelry is the signature */}
      <section className="relative min-h-[76vh] flex items-center" data-testid="myrna-hero">
        <img src="/myrna/hero.jpg" alt="Custom name jewelry from Myrna's Jewelry" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(30,91,79,0.94) 0%, rgba(30,91,79,0.65) 50%, rgba(30,91,79,0.1) 90%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#F0C88A" }}>Chinatown Gold Center, Stall 17-B</span>
            <h1 className="mt-5 text-4xl sm:text-5xl myrna-serif text-white leading-[1.1]">Custom Name Pieces, Wedding Bands &amp; Trusted Repairs</h1>
            <p className="mt-5 text-white/80 max-w-md">A name necklace, a fixed clasp, a wedding band made to fit — Myrna's has been doing this for years.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#custom" data-testid="myrna-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold" style={{ background: TERRA, color: "white" }}>Create Something Personal</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="myrna-hero-message" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.p {...fadeUp} className="text-2xl myrna-serif leading-relaxed" style={{ color: TEAL }}>{BIZ.intro}</motion.p>
      </section>

      {/* CUSTOM — the signature product type */}
      <section id="custom" className="py-16" style={{ background: "#EFE6D3" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="myrna-serif text-3xl text-center mb-12" style={{ color: TEAL }}>Made to Order, Made for You</motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <motion.div key={s.title} {...fadeUp} className="p-6 bg-white shadow-sm">
                <h3 className="font-semibold" style={{ color: TEAL }}>{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#4A4030]/80">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR WORK — gallery */}
      <section id="work" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="myrna-serif text-3xl text-center mb-12" style={{ color: TEAL }}>Our Work</motion.h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => (
            <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="myrna-product-card" className="bg-white shadow-sm group">
              <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-4">
                <h3 className="text-sm font-semibold">{p.name}</h3>
                <p className="mt-1 text-xs text-[#4A4030]/70">{p.desc}</p>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* REVIEWS — real customer pickup photo */}
      <section id="reviews" className="py-16" style={{ background: TEAL }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <motion.h2 {...fadeUp} className="myrna-serif text-3xl text-white mb-10">Happy Pickups</motion.h2>
          <motion.img {...fadeUp} src="/myrna/about-trust.jpg" alt="A real custom wedding-band pickup at Myrna's Jewelry" className="w-full max-w-xl mx-auto shadow-lg" />
          <p className="mt-6 text-white/70 text-sm">A real order, made and picked up in-store.</p>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 grid sm:grid-cols-4 gap-6 text-center">
        {STEPS.map((s) => (
          <motion.div key={s.n} {...fadeUp}>
            <div className="myrna-serif text-2xl" style={{ color: TERRA }}>{s.n}</div>
            <p className="mt-2 font-semibold text-sm">{s.title}</p>
            <p className="mt-1 text-xs text-[#4A4030]/70">{s.text}</p>
          </motion.div>
        ))}
      </section>

      {/* VISIT */}
      <section id="visit" className="py-16" style={{ background: "#EFE6D3" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="myrna-serif text-3xl" style={{ color: TEAL }}>Find Us</h2>
            <ul className="mt-7 space-y-5">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: TEAL }} className="mt-0.5" />{BIZ.address}</li>
              <li className="flex items-start gap-4"><Clock size={20} style={{ color: TEAL }} className="mt-0.5" />{BIZ.hours}</li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="myrna-facebook-link" className="hover:underline">myrnasjewelry</a></li>
              <li className="flex items-start gap-4"><Navigation size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={BIZ.waze} target="_blank" rel="noopener noreferrer" data-testid="myrna-waze-link" className="hover:underline">Get directions on Waze</a></li>
              <li className="flex items-start gap-4"><Youtube size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={BIZ.youtube1} target="_blank" rel="noopener noreferrer" data-testid="myrna-youtube-link" className="hover:underline">See a customer video</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[300px] rounded-xl overflow-hidden shadow-md"><iframe title="Myrna's Jewelry map" src={BIZ.mapEmbed} className="w-full h-full min-h-[300px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
        </div>
      </section>

      <footer className="py-8 bg-[#FAF4E9]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#5A5040]">
          <p className="myrna-serif text-base" style={{ color: TEAL }}>Myrna's Jewelry</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing and hours before visiting.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="myrna-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 rounded-full px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: TERRA }}><MessageCircle size={17} /> Message Myrna's</a>
      <ConceptCTAPill slug="myrnas-jewelry-concept" />
    </div>
  );
}
